import { useState, useRef, useEffect } from "react";
import { X, ExternalLink, Info, Maximize2, Minimize2, Clock, AlertCircle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { extractIframeSrc, deriveEditUrl } from "./SpreadsheetLinkList";

interface SharePointFileInfo {
  modified_by: string;
  modified_by_email: string;
  modified_at: string;
  file_name: string;
}

function useSharePointFileInfo(embedCode: string, src: string | null) {
  return useQuery<SharePointFileInfo | null>({
    queryKey: ["sharepoint-file-info", embedCode],
    queryFn: async () => {
      if (!src || !src.includes("sharepoint.com")) return null;
      const resp = await apiClient.get<{ data: SharePointFileInfo }>(
        "/tasks/sharepoint-file-info/",
        { params: { embed_code: embedCode } }
      );
      return resp.data.data;
    },
    enabled: !!src && src.includes("sharepoint.com"),
    staleTime: 1000 * 60 * 5,
    retry: false,
  });
}

function formatModifiedAt(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("pl-PL", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

interface Props {
  name: string;
  embedCode: string;
  onClose: () => void;
}

export function SpreadsheetPreviewModal({ name, embedCode, onClose }: Props) {
  const src = extractIframeSrc(embedCode);
  const editUrl = deriveEditUrl(embedCode);
  const [loaded, setLoaded] = useState(false);
  const [iframeBlocked, setIframeBlocked] = useState(false);
  const [blockDismissed, setBlockDismissed] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const loadStartRef = useRef(Date.now());
  const iframeLoadedRef = useRef(false);

  // Timeout fallback: if iframe never fires onLoad within 15s, assume blocked
  useEffect(() => {
    if (!src) return;
    loadStartRef.current = Date.now();
    iframeLoadedRef.current = false;
    const t = setTimeout(() => {
      if (!iframeLoadedRef.current) setIframeBlocked(true);
    }, 15_000);
    return () => clearTimeout(t);
  }, [src]);

  function handleIframeLoad() {
    iframeLoadedRef.current = true;
    const elapsed = Date.now() - loadStartRef.current;
    setLoaded(true);
    // Error/login pages load in milliseconds; real SharePoint files take longer
    if (src?.includes("sharepoint.com") && elapsed < 1500) {
      setIframeBlocked(true);
    }
  }

  const showBlockedOverlay = iframeBlocked && !blockDismissed;

  const { data: fileInfo } = useSharePointFileInfo(embedCode, src);

  return (
    <div className="fixed inset-0 z-[200] flex flex-col">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div
        className={
          isFullscreen
            ? "relative z-10 flex flex-col w-full h-full bg-white dark:bg-gray-900 shadow-2xl overflow-hidden"
            : "relative z-10 flex flex-col w-full h-full max-w-6xl max-h-[90vh] m-auto bg-white dark:bg-gray-900 rounded-xl shadow-2xl overflow-hidden"
        }
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex flex-col min-w-0">
            <span className="font-medium text-gray-800 dark:text-gray-100 truncate max-w-[60vw]">
              {name}
            </span>
            {fileInfo?.modified_by && (
              <span className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                <Clock size={11} />
                Ostatnia zmiana: <strong className="text-gray-600 dark:text-gray-300">{fileInfo.modified_by}</strong>
                &nbsp;·&nbsp;{formatModifiedAt(fileInfo.modified_at)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-4">
            {editUrl && (
              <a
                href={editUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs text-[var(--color-primary)] hover:underline"
              >
                <ExternalLink size={13} />
                Otwórz do edycji
              </a>
            )}
            <button
              onClick={() => setIsFullscreen((v) => !v)}
              title={isFullscreen ? "Zmniejsz" : "Pełny ekran"}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
            <button
              onClick={onClose}
              title="Zamknij"
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Info bar — visible only until iframe loads */}
        {!loaded && src && (
          <div className="flex items-center gap-2 px-4 py-1.5 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800 flex-shrink-0">
            <Info size={13} className="text-blue-500 flex-shrink-0" />
            <p className="text-xs text-blue-700 dark:text-blue-300">
              Podgląd wymaga aktywnej sesji Microsoft 365 w przeglądarce.
            </p>
          </div>
        )}

        {/* Iframe */}
        <div className="relative flex-1 min-h-0">
          {!loaded && src && (
            <div className="absolute inset-0 flex items-center justify-center bg-white dark:bg-gray-900 z-10">
              <div className="flex flex-col items-center gap-2 text-gray-400 dark:text-gray-500">
                <div className="w-6 h-6 border-2 border-gray-300 border-t-[var(--color-primary)] rounded-full animate-spin" />
                <span className="text-xs">Ładowanie podglądu…</span>
              </div>
            </div>
          )}
          {showBlockedOverlay && (
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-4 p-8 bg-white/97 dark:bg-gray-900/97">
              <AlertCircle size={40} className="text-amber-500 flex-shrink-0" />
              <div className="text-center max-w-sm">
                <p className="font-medium text-gray-800 dark:text-gray-100 mb-1">
                  Przeglądarka zablokowała podgląd
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  SharePoint nie zezwala na osadzanie arkusza w ramce. Otwórz plik bezpośrednio w nowej karcie.
                </p>
              </div>
              <div className="flex items-center gap-3">
                {editUrl && (
                  <a
                    href={editUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white rounded-lg hover:opacity-90 text-sm font-medium"
                  >
                    <ExternalLink size={15} />
                    Otwórz w SharePoint
                  </a>
                )}
                <button
                  onClick={() => setBlockDismissed(true)}
                  className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Pokaż ramkę
                </button>
              </div>
            </div>
          )}
          {src ? (
            <iframe
              src={src}
              title={name}
              className="w-full h-full border-0"
              onLoad={handleIframeLoad}
              allow="clipboard-write"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-sm text-gray-400 dark:text-gray-500 px-8 text-center">
              Nie można wyświetlić podglądu — zapisany kod nie zawiera atrybutu{" "}
              <code className="mx-1 px-1 bg-gray-100 dark:bg-gray-700 rounded">src</code>.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
